import { Box, DialogContent } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { modalStyle } from "src/constants/personProfile";
import { INotesClient } from "src/constants/types";
import { createOnSubmitNotes } from "src/utils/createOnSubmit";
import { notesDataTableClient } from "src/utils/personProfileDataTable";
import DatePicker from "src/v2/components/atoms/DatePicker/DatePicker";
import { TextField } from "src/v2/components/atoms/TextField";
import CustomAlert from "src/v2/components/molecules/Alert/CustomAlert";
import DialogModal from "src/v2/components/organisms/Dialog/Dialog";
import { UserInformation } from "src/v2/components/organisms/UserInformationTables/UserInformation";
import {
  DIALOG_BACKGROUND_COLOR,
} from "src/v2/constants/colors";
import { Dict } from "src/v2/models";

export function ClientProjectSection({
  personInformationNoteClient,
  user,
}: {
  personInformationNoteClient: Dict;
  user: string | undefined;
}) {
  const objectClient: INotesClient[] = personInformationNoteClient
    ? Object.values(personInformationNoteClient).filter(
        (object) => object.section === "Client"
      )
    : [];

  const [informationNoteClient, setInformationNoteClient] = useState(
    objectClient ?? []
  );

  useEffect(() => {
    if (personInformationNoteClient) setInformationNoteClient(objectClient);
  }, [personInformationNoteClient]);

  const { personId } = useParams();
  const [open, setOpen] = useState(false);
  const handleToggle = () => setOpen(!open);
  const [actionButton, setActionButton] = useState("");
  const [actionTitle, setActionTitle] = useState("");
  const [noteBeingEdited, setNoteBeingEdited] = useState({
    id: "",
    date: "",
    author: "",
    category: "",
    note: "",
    section: "Client",
  });
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [alertInformation, setAlertInformation] = useState({
    severity: "success",
    message: "Data saved correctly",
  });
  const properties = {
    displayCheckbox: false,
    hidePagination: true,
    hideFooter: true,
    customToolBar: false,
    tableContainer: { maxWidth: "100%" },
  };

  const [savedData, setSavedData] = useState(false);
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<INotesClient>({ shouldUnregister: true });

  const onSubmit = createOnSubmitNotes({
    onSuccess: (response) => {
      setAlertInformation({
        severity: "success",
        message: "Data saved correctly",
      });
      setInformationNoteClient(Object.values(response.data.groupedNotes));
      setSavedData(true);
    },
    onError: () => {
      setAlertInformation({
        severity: "error",
        message: "Error data was not saved",
      });
      setSavedData(true);
    },
    onSettled: () => {
      handleToggle();
      reset();
    },
  });
  const handleSubmitForm = handleSubmit(async (formData) => {
    const editId = noteBeingEdited.id;
    const noteData = {
      ...formData,
      date: dayjs(formData.createdAt).format("YYYY-MM-DD"),
      section: "Client",
    };
    const notes = isEditingStatus
      ? { [editId]: noteData }
      : { newNote: noteData };
    const dataHR: any = {
      person_id: personId,
      custom_fields: {
        notes: {
          ...notes,
        },
      },
    };
    await onSubmit(dataHR);
  });

  const handleEditClick = (noteId: string) => {
    setNoteBeingEdited(
      informationNoteClient.filter((note) => noteId === note.id)[0]
    );
    setIsEditingStatus(true);
    setActionButton("SAVE");
    setActionTitle("Editing Client Note");
    handleToggle();
  };

  const handleExit = () => {
    handleToggle();
    reset();
  };
  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSavedData(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <UserInformation
        title='Client/Project'
        onClick={() => {
          setIsEditingStatus(false);
          setActionButton("SAVE");
          setActionTitle("Client/Project");
          handleToggle();
        }}
        columns={notesDataTableClient(handleEditClick, user)}
        rows={informationNoteClient}
        properties={properties}
      />
      <Box data-testid='modal-dialog'>
        <DialogModal
          open={open}
          handleCloseAndSave={handleSubmitForm}
          title={actionTitle}
          isSaving={isSubmitting}
          buttonTitle={actionButton}
          handleExit={handleExit}
          form={
            <DialogContent sx={{ background: DIALOG_BACKGROUND_COLOR }}>
              <div style={modalStyle}>
                <Controller
                  control={control}
                  defaultValue={
                    isEditingStatus ? dayjs(noteBeingEdited.date) : dayjs()
                  }
                  render={({ field: { onChange, value } }) => {
                    const startYear = dayjs(value).add(-5, "year");
                    const endYear = dayjs(value);
                    return (
                      <DatePicker
                        label='Date'
                        value={dayjs(value)}
                        minDate={startYear}
                        maxDate={endYear}
                        sx={{ width: "20%" }}
                        onChange={onChange}
                        disableFuture={!false}
                      />
                    );
                  }}
                  name='createdAt'
                />
              </div>
              <div style={modalStyle}>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  defaultValue={user}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      id='authorId'
                      variant='outlined'
                      label='Author'
                      value={value}
                      read={false}
                      size='medium'
                      sx={{ width: { md: "100%" } }}
                      onChange={onChange}
                      disabled
                    />
                  )}
                  name='author'
                />
                <Controller
                  control={control}
                  rules={{ required: true }}
                  defaultValue={isEditingStatus ? noteBeingEdited.category : ""}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      id='categoryId'
                      variant='outlined'
                      label='Client'
                      helperText={errors.category ? "Client is required" : ""}
                      read={false}
                      value={value}
                      size='medium'
                      sx={{ width: { md: "100%" } }}
                      onChange={onChange}
                    />
                  )}
                  name='category'
                />
              </div>
              <div style={modalStyle}>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  defaultValue={isEditingStatus ? noteBeingEdited.note : ""}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      id='notesId'
                      variant='outlined'
                      label='Notes'
                      helperText={errors.note ? "Note is required" : ""}
                      read={false}
                      value={value}
                      size='medium'
                      multiline
                      maxRows={5}
                      sx={{ width: { md: "100%" } }}
                      onChange={onChange}
                    />
                  )}
                  name='note'
                />
              </div>
            </DialogContent>
          }
        />
      </Box>
      <CustomAlert
        sx={{ marginLeft: "50px" }}
        text={alertInformation.message}
        severity={
          alertInformation.severity as "error" | "success" | "info" | "warning"
        }
        open={savedData}
        onClose={handleClose}
      />
    </Box>
  );
}
